/**
 * Runtime brand accent.
 *
 * History:
 *  1. Build-time only — a data-accent attr + accent.css ("one file,
 *     one commit").
 *  2. Admin-controlled runtime global — a row in public.app_settings
 *     served by GET /api/settings/accent (migration 020).
 *  3. Per-user — every member picks their own accent from a persistent
 *     picker (migration 022 adds profiles.accent / accent_strong).
 *
 * PRECEDENCE (resolved here, client-side):
 *   personal pick (profiles.accent)
 *     → admin global (app_settings 'accent')   — the default
 *       → null / null                          — pure black-&-white
 *
 * The token chain is unchanged — tokens.css reads
 * `var(--accent-override, …)`; we set `--accent-override` /
 * `--accent-strong-override` inline on <html>, which wins over both the
 * accent.css presets and the carbon/paper fallbacks. Only *who owns the
 * value* changed; the CSS cascade did not.
 *
 * No-FOUC: index.html has a tiny inline script that paints the
 * localStorage-cached value (`cc:accent` — always the last *effective*
 * accent, personal or global) before first paint. The admin global is
 * cached separately (`cc:accent:global`) so the session layer can resolve
 * precedence without a network round-trip. session.ts calls
 * applyEffectiveAccent() once the profile resolves; the sign-in page
 * (no profile) shows the global.
 */

export interface AccentValue {
  accent: string | null;
  accent_strong: string | null;
}

export const ACCENT_STORAGE_KEY = 'cc:accent';
/** The admin global accent, cached so precedence resolves offline / pre-fetch. */
export const GLOBAL_ACCENT_STORAGE_KEY = 'cc:accent:global';
const ACCENT_VAR = '--accent-override';
const ACCENT_STRONG_VAR = '--accent-strong-override';
const ACCENT_INK_VAR = '--accent-ink-override';

/**
 * Pick a readable text colour ('ink') to sit ON a solid accent fill. Light
 * accents → carbon (dark text); dark accents → paper (light text). Uses the
 * YIQ brightness heuristic. Keep in sync with the inline boot script in
 * index.html. Returns null for an unparseable colour (caller leaves the ink
 * override unset → falls back to paper).
 */
export function accentInk(hex: string | null): string | null {
  if (!hex) return null;
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = Number.parseInt(m[1], 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness >= 150 ? '#14110F' : '#FAFAF7';
}

export const NO_ACCENT: AccentValue = { accent: null, accent_strong: null };

/** In-memory mirror of the admin global accent (avoids re-reading storage). */
let globalAccentCache: AccentValue = NO_ACCENT;

/**
 * Parse + normalise a colour to lowercase #rrggbb, or null for "no
 * accent" (empty/null input). Throws on invalid input. Mirrors the
 * server-side validator in functions/api/settings/accent.ts so the admin
 * UI can validate before sending.
 */
export function parseColour(input: string | null | undefined): string | null {
  if (input === null || input === undefined) return null;
  const raw = String(input).trim().toLowerCase();
  if (raw === '') return null;

  const hex = raw.match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/);
  if (hex) {
    let h = hex[1];
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    return `#${h}`;
  }

  const rgb = raw.match(
    /^rgba?\(\s*(\d{1,3})[\s,]+(\d{1,3})[\s,]+(\d{1,3})\s*(?:[\s,/]+[\d.]+%?\s*)?\)$/,
  );
  if (rgb) {
    const parts = [rgb[1], rgb[2], rgb[3]].map((n) => Number.parseInt(n, 10));
    if (parts.some((n) => n < 0 || n > 255)) {
      throw new Error('RGB channels must be 0–255.');
    }
    return `#${parts.map((n) => n.toString(16).padStart(2, '0')).join('')}`;
  }

  throw new Error('Use #rgb, #rrggbb, or rgb(r, g, b).');
}

/** Apply (or clear) the accent on <html> and cache it for no-FOUC reloads. */
export function applyAccent(value: AccentValue): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const accent = value.accent;
  const strong = value.accent_strong ?? value.accent;

  if (accent) {
    root.style.setProperty(ACCENT_VAR, accent);
    root.style.setProperty(ACCENT_STRONG_VAR, strong ?? accent);
    const ink = accentInk(accent);
    if (ink) root.style.setProperty(ACCENT_INK_VAR, ink);
    else root.style.removeProperty(ACCENT_INK_VAR);
  } else {
    root.style.removeProperty(ACCENT_VAR);
    root.style.removeProperty(ACCENT_STRONG_VAR);
    root.style.removeProperty(ACCENT_INK_VAR);
  }

  try {
    if (accent) {
      window.localStorage.setItem(
        ACCENT_STORAGE_KEY,
        JSON.stringify({ accent, accent_strong: strong ?? accent }),
      );
    } else {
      window.localStorage.removeItem(ACCENT_STORAGE_KEY);
    }
  } catch {
    /* private mode / storage disabled — accent still applied for this load */
  }
}

/** The cached accent from a previous load (used by the inline boot script too). */
export function readCachedAccent(): AccentValue {
  try {
    const raw = window.localStorage.getItem(ACCENT_STORAGE_KEY);
    if (!raw) return NO_ACCENT;
    const v = JSON.parse(raw) as Partial<AccentValue>;
    return {
      accent: typeof v.accent === 'string' && v.accent ? v.accent : null,
      accent_strong:
        typeof v.accent_strong === 'string' && v.accent_strong ? v.accent_strong : null,
    };
  } catch {
    return NO_ACCENT;
  }
}

/** Fetch the authoritative accent. Never throws — falls back to B&W. */
export async function fetchAccent(signal?: AbortSignal): Promise<AccentValue> {
  try {
    const res = await fetch('/api/settings/accent', { signal });
    if (!res.ok) return NO_ACCENT;
    const v = (await res.json()) as Partial<AccentValue>;
    return {
      accent: typeof v.accent === 'string' && v.accent ? v.accent : null,
      accent_strong:
        typeof v.accent_strong === 'string' && v.accent_strong ? v.accent_strong : null,
    };
  } catch {
    return NO_ACCENT;
  }
}

/** Admin save: PUT the new accent. Returns the normalised stored value. */
export async function saveAccent(
  value: AccentValue,
  accessToken: string,
): Promise<AccentValue> {
  const res = await fetch('/api/settings/accent', {
    method: 'PUT',
    headers: {
      'content-type': 'application/json',
      authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(value),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = (data as any)?.error ?? `Save failed (${res.status}).`;
    const detail = (data as any)?.detail;
    throw new Error(detail ? `${err}: ${detail}` : err);
  }
  return data as AccentValue;
}

/** Read the cached admin global accent (sync — used before the fetch lands). */
export function readCachedGlobalAccent(): AccentValue {
  if (globalAccentCache.accent) return globalAccentCache;
  try {
    const raw = window.localStorage.getItem(GLOBAL_ACCENT_STORAGE_KEY);
    if (!raw) return NO_ACCENT;
    const v = JSON.parse(raw) as Partial<AccentValue>;
    globalAccentCache = {
      accent: typeof v.accent === 'string' && v.accent ? v.accent : null,
      accent_strong:
        typeof v.accent_strong === 'string' && v.accent_strong ? v.accent_strong : null,
    };
    return globalAccentCache;
  } catch {
    return NO_ACCENT;
  }
}

/** Fetch the admin global accent and cache it (memory + localStorage). */
export async function loadGlobalAccent(): Promise<AccentValue> {
  const value = await fetchAccent();
  globalAccentCache = value;
  try {
    if (value.accent) {
      window.localStorage.setItem(GLOBAL_ACCENT_STORAGE_KEY, JSON.stringify(value));
    } else {
      window.localStorage.removeItem(GLOBAL_ACCENT_STORAGE_KEY);
    }
  } catch {
    /* storage disabled — in-memory cache still serves this load */
  }
  return value;
}

/**
 * Apply precedence: a member's personal pick wins; otherwise the admin
 * global default; otherwise pure black-&-white. `personal`/`global`
 * default to the caches so callers can pass only what they know.
 */
export function applyEffectiveAccent(
  personal: AccentValue | null | undefined,
  global: AccentValue = readCachedGlobalAccent(),
): AccentValue {
  const effective =
    personal && personal.accent
      ? { accent: personal.accent, accent_strong: personal.accent_strong ?? personal.accent }
      : global.accent
        ? global
        : NO_ACCENT;
  applyAccent(effective);
  return effective;
}

/**
 * Boot-time (pre-auth) reconcile. Called from main.tsx before the session
 * resolves. The inline script in index.html has already painted the
 * cached effective accent (`cc:accent`), so to avoid a personal→global
 * flash we only repaint here on a cold start (no cached accent — first
 * visit or cleared storage). For authed users session.ts then calls
 * applyEffectiveAccent() with the freshly-loaded profile; the signed-out
 * sign-in page keeps the global we just cached.
 */
export async function initAccent(): Promise<void> {
  const global = await loadGlobalAccent();
  const cached = readCachedAccent();
  if (!cached.accent) applyAccent(global);
}
